import { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';
import { ApolloProvider } from 'react-apollo';

import { QueryInterop as Query } from '@confluence/apollo-relay-interop/entry-points/QueryInterop.ts';

import { FormattedMessage } from 'react-intl-next';

import type { ExtensionParams } from '@atlaskit/editor-common/extensions';
import type { ADNode } from '@atlaskit/editor-common/validator';
import { Text } from '@atlaskit/primitives/compiled';

import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { LegacyMacroRendererShadow } from '@confluence/content-renderer-legacy-macros/entry-points/LegacyMacroRendererShadow.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';
import {
	extensionToADF,
	getParametersString,
} from '@confluence/fabric-extension-lib/entry-points/editor-extensions.ts';
import {
	BODIED_EXTENSION_TYPE,
	REGULAR_EXTENSION_TYPE,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import type { WebResources } from '@confluence/fabric-extension-lib/entry-points/macro-loaders.ts';
import { is2PBodiedExtension } from '@confluence/fabric-extension-lib/entry-points/useExtensionRendererViewOptions.ts';
import { canUseMultiMacroQuery } from '@confluence/fabric-extension-queries/entry-points/useMultiMacroQuery.ts';
import { MultiMacroQuerySubscriber } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { serializeExtensionADF } from '@confluence/fabric-extension-queries/entry-points/serializeExtensionADF.ts';
import { getContentConverterVariables } from '@confluence/fabric-extension-queries/entry-points/getContentConverterVariables.ts';
import { ContentConverterQuery } from '@confluence/fabric-extension-queries/entry-points/ContentConverterQuery.graphql.ts';
import {
	LegacyMacroRendererQueryBodyQuery,
	type LegacyMacroRendererQueryBodyQuery$data,
	type LegacyMacroRendererQueryBodyQuery$variables,
} from '@confluence/fabric-extension-queries/entry-points/LegacyMacroRendererQueryBodyQuery.graphql.ts';
import {
	LegacyMacroRendererQueryContentQuery,
	type LegacyMacroRendererQueryContentQuery$data,
	type LegacyMacroRendererQueryContentQuery$variables,
} from '@confluence/fabric-extension-queries/entry-points/LegacyMacroRendererQueryContentQuery.graphql.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { getLogger } from '@confluence/logger';
import type { Extension } from '@confluence/macro-tracker/entry-points/types.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
	stopJIRAMacro,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { debounceRender } from '@confluence/react-debounce-render';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';

import { i18n } from './i18n.ts';
import { LegacyMacroRendererComponent } from './LegacyMacroRendererComponent.tsx';
import { LegacyMacroRendererContext } from './LegacyMacroRendererContext.tsx';
import { LegacyMacroRendererError } from './LegacyMacroRendererError.tsx';
import type { QueryNode, QueryProps } from './macroRendererTypes.ts';
import type { ExtensionWithParentContentId } from './PageIncludeContext.ts';
import { PageIncludeContext } from './PageIncludeContext.ts';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import type {
	ContentConverterQueryTypes,
	ContentConverterQueryVariables,
} from '@confluence/fabric-extension-queries/entry-points/ContentConverterQuery.ts';

/**
 * Checks whether an extension node is a plain text macro or not.
 *
 * The node at this point may not indicate what it's content type is, so a
 * reliable way is to check for the `__bodyContent` property, which exists
 * on plain text macros.
 *
 * @param node Extension node to check
 */
const isPlainTextMacro = (node: Extension): boolean =>
	node.type === REGULAR_EXTENSION_TYPE && Boolean(node?.parameters?.macroParams?.__bodyContent);

/**
 * All bodied extensions are rich text.
 *
 * @param node Extension node to check
 */
const isRichTextMacro = (node: Extension): boolean => node.type === BODIED_EXTENSION_TYPE;

type ADFContent = (ADNode | QueryNode) | (ADNode | QueryNode)[];

const convertADFContentToStorageQuery = async (
	contentId: string,
	content: ADFContent,
): Promise<string> => {
	const result = await queryInterop<ContentConverterQueryTypes, ContentConverterQueryVariables>({
		query: ContentConverterQuery,
		variables: getContentConverterVariables({
			content,
			contentId,
		}),
	});

	return result.data?.contentConverter?.value ?? '';
};

type State = {
	body: string;
	hasBodyBeenFetched: boolean;
};

function isMacroUsingInEditorRendererView({
	isInEditorRendererView,
	node,
	extensionKey,
}: QueryProps) {
	return (
		isInEditorRendererView &&
		is2PBodiedExtension({
			extensionKey,
			extensionType: node.type,
		})
	);
}

function isIncludePageNested2pBodiedExtension({
	contentId,
	node,
	extensionKey,
}: QueryProps): boolean {
	const hasParent: boolean = !!node?.parameters?.macroParams?._parentId?.value;
	const hasSameParent: boolean = node?.parameters?.macroParams?._parentId?.value === contentId;
	return (
		is2PBodiedExtension({ extensionKey, extensionType: node.type }) &&
		hasParent &&
		!!contentId &&
		!hasSameParent
	);
}

function shouldConvertBodyToStorageFormat(props: QueryProps): boolean {
	const { extensionKey } = props;
	return Boolean(
		!canUseMultiMacroQuery(extensionKey, isIncludePageNested2pBodiedExtension(props)) ||
		isMacroUsingInEditorRendererView(props),
	);
}

export class LegacyMacroRendererQueryComponent extends Component<QueryProps, State> {
	constructor(props: QueryProps) {
		super(props);

		const state: State = {
			body: '',
			hasBodyBeenFetched: false,
		};

		// Check condition upfront to avoid unnecessary state updates
		// Otherwise it will make component to always render the shadow which breaks SSR result
		// Basically repeat of setMacroData logic for the initial render
		const { node } = props;
		if (isPlainTextMacro(node)) {
			state.body = node?.parameters?.macroParams?.__bodyContent?.value || '';
			state.hasBodyBeenFetched = true;
		} else if (isRichTextMacro(node)) {
			if (!shouldConvertBodyToStorageFormat(props)) {
				state.hasBodyBeenFetched = true;
			}
		} else {
			state.hasBodyBeenFetched = true;
		}

		this.state = state;
	}

	componentDidMount() {
		this.setMacroData();
		this.setUFOEventMacroDisplayStatus();
	}

	shouldComponentUpdate(nextProps: QueryProps, nextState: State) {
		return (
			this.props.contentId !== nextProps.contentId ||
			!isEqual(this.props?.node?.parameters, nextProps?.node?.parameters) ||
			((nextProps.references || this.props.references) &&
				this.props.references !== nextProps.references) ||
			this.state.hasBodyBeenFetched !== nextState.hasBodyBeenFetched ||
			this.state.body !== nextState.body
		);
	}

	componentDidUpdate() {
		this.setMacroData();
	}

	componentWillUnmount(): void {
		const { node, mode, contentId } = this.props;

		const attributes = getMacroAttributesFromADFNode(node);

		stopJIRAMacro(mode, contentId, name, attributes);
	}

	logger = getLogger('LegacyMacroRendererQuery');

	/**
	 * Parses the rich text macro's body and sets the state with it.
	 */
	async setRichTextMacroBody() {
		const { contentId, node } = this.props;

		// Get the ADF content of the node
		const content = (node?.content || []) as ADFContent;

		try {
			// Unfortunately, vendor add ons expect storage format. We need to convert
			// the ADF to the appropriate format.

			let body;
			// only use conversion query in two scenarios
			//   1. if not using macros query where body storage format is already provided.
			//   2. If macro is using in editor renderer view, we need to update the body for this singular macro
			//      using the multi macro query would refetch for all macros on the page, re-rendering them all.
			if (shouldConvertBodyToStorageFormat(this.props)) {
				body = await convertADFContentToStorageQuery(contentId, content);
			}

			this.setState({
				body,
				hasBodyBeenFetched: true,
			});
		} catch (error) {
			this.logger.error`${error}`; //tslint:disable-line
		}
	}

	/**
	 * Parses the plain text macro's body and sets the state with it.
	 *
	 * Even though it's plain text, we don't actually need to set state. This
	 * could be done in the renderer, but because we're converting rich text
	 * macro's body to storage format, the mechanism for passing in the body is
	 * `this.setState`. This will change in the future if we start passing
	 * adf in our `getMacroBody` calls (and then this can be cleaned up).
	 */
	setPlainTextMacroBody() {
		const { node } = this.props;

		const body = node?.parameters?.macroParams?.__bodyContent?.value || '';

		this.setState({
			body,
			hasBodyBeenFetched: true,
		});
	}

	setMacroData() {
		const { node } = this.props;
		// 1. Node is null, which means we won't render anything.
		// 2. If macro is using in editor renderer view, we need to refetch the body on update
		//    because the parameters might have changed due to user interaction in the macros edit mode.
		if (
			!node ||
			(isRichTextMacro(node) &&
				this.state.hasBodyBeenFetched &&
				!isMacroUsingInEditorRendererView(this.props))
		) {
			return;
		}

		if (isPlainTextMacro(node)) {
			this.setPlainTextMacroBody();
		} else if (isRichTextMacro(node)) {
			void this.setRichTextMacroBody();
		} else {
			// It's neither, so just render the component
			this.setState({
				hasBodyBeenFetched: true,
			});
		}
	}

	setUFOEventMacroDisplayStatus() {
		const extensionKey = this.props.extensionKey;
		if (!extensionKey) return;

		const parts = extensionKey.split('-');
		const pascalCaseString = parts
			.map((part) => {
				if (part.length === 0) {
					return '';
				}
				return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
			})
			.join('');
		addUFOCustomData({
			[`is${pascalCaseString}MacroDisplayed`]: true,
		});
	}

	// Use parent id if the macro source is different than the current page.
	// Critical for having the include-page macro load properly
	getContentId(adf, contentId) {
		const parentId = adf?.attrs?.parameters?.macroParams?._parentId?.value;
		return parentId || contentId;
	}

	/**
	 * Return a query for both contentRenderer and macroBodyRenderer.
	 */
	getQueryForMacroContentAndBodyForPossibleFrontendRendering = ({
		adf,
		contentId,
		extensionKey,
		mode,
		node,
		references,
		multiBodiedExtensionActions,
		featureFlags,
		macroRenderedOutputFromSSR,
		macroOutput,
		spaceKey,
		shouldRefetchMacroQuery,
		noOverlay,
		isPreviewMode,
	}) => {
		const renderPageIncludeError = (pageTitle) => {
			return (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<div className="error">
					<Text>
						<FormattedMessage {...i18n.highlightedPageIncludeRenderedError} />
					</Text>
					<FormattedMessage
						{...i18n.nonhighlightedPageIncludeRenderedError}
						values={{ pageTitle }}
					/>
				</div>
			);
		};

		return (
			<Query<LegacyMacroRendererQueryBodyQuery$data, LegacyMacroRendererQueryBodyQuery$variables>
				query={LegacyMacroRendererQueryBodyQuery}
				variables={{
					adf: serializeExtensionADF(adf),
					// The template editor passes a "0" as contentId by default,
					// in this scenario we do NOT want to pass a contentId at all into the query.
					// Not passing the correct contentId can make some macros fail to load
					...(contentId !== '0' ? { contentId: this.getContentId(adf, contentId) } : {}),
					mode: mode && mode.toUpperCase(),
				}}
				// Used when multiple instances of PageContentRenderer cause conflicting IDs in macroRenderedOutput. This forces
				// a refetch when rendering subsequent PageContentRenderer instances so that a new ID is present in
				// macroRenderedOutput. 'cache-first' is Apollo's default fetchPolicy.
				fetchPolicy={shouldRefetchMacroQuery ? 'no-cache' : 'cache-first'}
				// Some macros take a long time to render, they should not hold up the rendering of other things
				context={{
					throttle: true,
					allowOnExternalPage: true,
				}}
				skip={Boolean(macroRenderedOutputFromSSR) || Boolean(macroOutput)}
			>
				{({ data, loading, error }) => {
					// render LegacyMacroRendererComponent without query data if macro has been
					// SSR'd and macro output already exists or when macroOutput
					// exists which contains the entire html string needed to render the macro
					if (macroRenderedOutputFromSSR || macroOutput) {
						return (
							<LegacyMacroRendererComponent
								contentId={contentId}
								experienceName={getExperienceName(mode, node)}
								adf={adf}
								attributes={getMacroAttributesFromADFNode(node)}
								references={references}
								multiBodiedExtensionActions={multiBodiedExtensionActions}
								macroRenderedOutput={data?.macroBodyRenderer?.value}
								macroRepresentation={data?.macroBodyRenderer?.representation || 'view'}
								mediaToken={data?.macroBodyRenderer?.mediaToken?.token}
								node={node}
								webresource={data?.macroBodyRenderer?.webResourceDependencies as WebResources}
								mode={mode}
								body={this.state.body}
								parameters={getParametersString(node)}
								featureFlags={featureFlags}
								macroRenderedOutputFromSSR={macroRenderedOutputFromSSR}
								macroOutput={macroOutput}
								spaceKey={spaceKey}
								noOverlay={noOverlay}
								isPreviewMode={isPreviewMode}
							/>
						);
					}

					if (loading) {
						return <LegacyMacroRendererShadow showSpinner={!!data} node={node} />;
					}

					if (error || !data || !data.macroBodyRenderer) {
						return (
							<LegacyMacroRendererError
								error={error}
								node={node}
								mode={mode}
								extensionKey={extensionKey}
								contentId={contentId}
							/>
						);
					}

					// Make it possible to execute AC JS functions locally.
					// The hostOrigin should match browser host
					let macroRenderedOutput = data.macroBodyRenderer?.value;
					if (process.env.NODE_ENV === 'development' && macroRenderedOutput) {
						macroRenderedOutput = macroRenderedOutput.replace(
							/\"hostOrigin\":"([^"]+)"/,
							`"hostOrigin":"http://localhost:8081/"`,
						);
					}
					return (
						<PageIncludeContext.Consumer>
							{({ listOfRenderedPageIncludeNodes }) => {
								const { isCircularRef, prevPageTitle } = doesIncludePageCircularRefExist(
									listOfRenderedPageIncludeNodes,
								);
								if (isCircularRef) {
									return renderPageIncludeError(prevPageTitle);
								}

								return (
									<PageIncludeContextProvider
										contentId={contentId}
										node={this.props.node}
										listOfRenderedPageIncludeNodes={listOfRenderedPageIncludeNodes}
									>
										<LegacyMacroRendererComponent
											contentId={contentId}
											experienceName={getExperienceName(mode, node)}
											adf={adf}
											attributes={getMacroAttributesFromADFNode(node)}
											references={references}
											multiBodiedExtensionActions={multiBodiedExtensionActions}
											macroRenderedOutput={macroRenderedOutput}
											macroRepresentation={data.macroBodyRenderer?.representation || 'view'}
											mediaToken={data?.macroBodyRenderer?.mediaToken?.token}
											node={node}
											webresource={data.macroBodyRenderer?.webResourceDependencies as WebResources}
											mode={mode}
											body={this.state.body}
											parameters={getParametersString(node)}
											featureFlags={featureFlags}
											spaceKey={spaceKey}
											noOverlay={noOverlay}
											isPreviewMode={isPreviewMode}
										/>
									</PageIncludeContextProvider>
								);
							}}
						</PageIncludeContext.Consumer>
					);
				}}
			</Query>
		);
	};

	getQueryForMacroContentFromBackend = ({
		adf,
		contentId,
		extensionKey,
		mode,
		node,
		references,
		multiBodiedExtensionActions,
		featureFlags,
		macroRenderedOutputFromSSR,
		macroOutput,
		spaceKey,
		shouldRefetchMacroQuery,
		noOverlay,
		isPreviewMode,
	}) => {
		return (
			<Query<
				LegacyMacroRendererQueryContentQuery$data,
				LegacyMacroRendererQueryContentQuery$variables
			>
				query={LegacyMacroRendererQueryContentQuery}
				variables={{
					adf: serializeExtensionADF(adf),
					contentId,
					mode: mode && mode.toUpperCase(),
				}}
				// Used when multiple instances of PageContentRenderer cause conflicting IDs in macroRenderedOutput. This forces
				// a refetch when rendering subsequent PageContentRenderer instances so that a new ID is present in
				// macroRenderedOutput. 'cache-first' is Apollo's default fetchPolicy.
				fetchPolicy={shouldRefetchMacroQuery ? 'no-cache' : 'cache-first'}
				// Some macros take a long time to render, they should not hold up the rendering of other things
				context={{
					throttle: true,
					allowOnExternalPage: true,
				}}
				skip={Boolean(macroRenderedOutputFromSSR) || Boolean(macroOutput)}
			>
				{({ data, loading, error }) => {
					// render LegacyMacroRendererComponent without query data if macro has been
					// SSR'd and macro output already exists or when macroOutput
					// exists which contains the entire html string needed to render the macro
					if (macroRenderedOutputFromSSR || macroOutput) {
						return (
							<LegacyMacroRendererComponent
								contentId={contentId}
								experienceName={getExperienceName(mode, node)}
								adf={adf}
								node={node}
								attributes={getMacroAttributesFromADFNode(node)}
								macroRenderedOutput={data?.contentRenderer?.html}
								references={references}
								multiBodiedExtensionActions={multiBodiedExtensionActions}
								macroRepresentation="view"
								webresource={data?.contentRenderer?.webResourceDependencies as WebResources}
								mode={mode}
								body={this.state.body}
								parameters={getParametersString(node)}
								featureFlags={featureFlags}
								macroRenderedOutputFromSSR={macroRenderedOutputFromSSR}
								macroOutput={macroOutput}
								spaceKey={spaceKey}
								noOverlay={noOverlay}
								isPreviewMode={isPreviewMode}
							/>
						);
					}

					if (loading) {
						return <LegacyMacroRendererShadow showSpinner={!!data} node={node} />;
					}
					if (error || !data || !data.contentRenderer) {
						return (
							<LegacyMacroRendererError
								error={error}
								node={node}
								mode={mode}
								extensionKey={extensionKey}
								contentId={contentId}
							/>
						);
					}

					// Make it possible to execute AC JS functions locally.
					// The hostOrigin should match browser host
					let contentRenderedHtml = data.contentRenderer.html;
					if (process.env.NODE_ENV === 'development' && contentRenderedHtml) {
						contentRenderedHtml = contentRenderedHtml.replace(
							/\"hostOrigin\":"([^"]+)"/,
							`"hostOrigin":"http://localhost:8081/"`,
						);
					}
					// assume we have data here
					return (
						<LegacyMacroRendererComponent
							contentId={contentId}
							experienceName={getExperienceName(mode, node)}
							adf={adf}
							node={node}
							attributes={getMacroAttributesFromADFNode(node)}
							macroRenderedOutput={contentRenderedHtml}
							references={references}
							multiBodiedExtensionActions={multiBodiedExtensionActions}
							macroRepresentation="view"
							webresource={data.contentRenderer.webResourceDependencies as WebResources}
							mode={mode}
							body={this.state.body}
							parameters={getParametersString(node)}
							featureFlags={featureFlags}
							noOverlay={noOverlay}
							isPreviewMode={isPreviewMode}
						/>
					);
				}}
			</Query>
		);
	};

	getQueryForMacros = (args) => {
		const {
			adf,
			contentId,
			extensionKey,
			mode,
			node,
			references,
			multiBodiedExtensionActions,
			featureFlags,
			macroRenderedOutputFromSSR,
			macroOutput,
			spaceKey,
			noOverlay,
		} = args;

		const macroId = node.parameters?.macroMetadata?.macroId?.value;
		return (
			<MultiMacroQuerySubscriber contentId={contentId} macroId={macroId}>
				{({ renderedMacro, loading, error, complete }) => {
					// if the multi macro query is complete and there were no errors
					// that means we are inserting a new macro in the editor or have
					// just published the page in which case the new macro data
					// will not be available on the backend to render yet so use
					// the old queries to render instead
					if (complete && !renderedMacro && !error) {
						return this.determineQuery(args, true);
					}

					// render LegacyMacroRendererComponent without query data if macro has been
					// SSR'd and macro output already exists or when macroOutput
					// exists which contains the entire html string needed to render the macro
					if (macroRenderedOutputFromSSR || macroOutput) {
						return (
							<LegacyMacroRendererComponent
								contentId={contentId}
								experienceName={getExperienceName(mode, node)}
								adf={adf}
								node={node}
								attributes={getMacroAttributesFromADFNode(node)}
								macroRenderedOutput={renderedMacro?.value}
								references={references}
								multiBodiedExtensionActions={multiBodiedExtensionActions}
								macroRepresentation={renderedMacro?.macroRenderedRepresentation || 'view'}
								webresource={renderedMacro?.webResource as WebResources}
								mode={mode}
								body={renderedMacro?.macroBodyStorage || this.state.body}
								parameters={getParametersString(node)}
								featureFlags={featureFlags}
								macroRenderedOutputFromSSR={macroRenderedOutputFromSSR}
								macroOutput={macroOutput}
								spaceKey={spaceKey}
								noOverlay={noOverlay}
								mediaToken={renderedMacro?.mediaToken?.token}
							/>
						);
					}

					if (loading) {
						return <LegacyMacroRendererShadow node={node} />;
					}

					if (error || !renderedMacro) {
						return (
							<LegacyMacroRendererError
								error={
									new ApolloErrorInterop({
										errorMessage: `error rendering legacy macro: {$extensionKey}`,
									})
								}
								node={node}
								mode={mode}
								extensionKey={extensionKey}
								contentId={contentId}
							/>
						);
					}

					// Make it possible to execute AC JS functions locally.
					// The hostOrigin should match browser host
					// DO WE NEED THIS ↓?
					if (process.env.NODE_ENV === 'development' && renderedMacro?.value) {
						renderedMacro.value = renderedMacro.value.replace(
							/\"hostOrigin\":"([^"]+)"/,
							`"hostOrigin":"http://localhost:8081/"`,
						);
					}

					// assume we have data here
					return (
						<LegacyMacroRendererComponent
							contentId={contentId}
							experienceName={getExperienceName(mode, node)}
							adf={adf}
							node={node}
							attributes={getMacroAttributesFromADFNode(node)}
							macroRenderedOutput={renderedMacro?.value}
							references={references}
							multiBodiedExtensionActions={multiBodiedExtensionActions}
							macroRepresentation={renderedMacro?.macroRenderedRepresentation || 'view'}
							webresource={renderedMacro?.webResource as WebResources}
							mode={mode}
							body={renderedMacro?.macroBodyStorage || this.state.body}
							parameters={getParametersString(node)}
							featureFlags={featureFlags}
							noOverlay={noOverlay}
							mediaToken={renderedMacro?.mediaToken?.token}
						/>
					);
				}}
			</MultiMacroQuerySubscriber>
		);
	};

	getReferencedContentId = (node) => {
		return node?.parameters?.macroParams?._referencedContentId?.value || undefined;
	};

	determineQuery = (props, doNotUseMultiMacro = false) => {
		const { extensionKey, node } = props;

		if (
			!doNotUseMultiMacro &&
			(!node?.parameters?.macroParams?._parentId ||
				node.parameters.macroParams._parentId.value === this.props.contentId) &&
			canUseMultiMacroQuery(extensionKey, isIncludePageNested2pBodiedExtension(props))
		) {
			return this.getQueryForMacros(props);
		}

		const editorMacroADFRendering = StatsigConfigurations.getBoolean(
			'confluence_frontend_macro_adf_frontend_render',
			true,
		);

		return editorMacroADFRendering
			? this.getQueryForMacroContentAndBodyForPossibleFrontendRendering(props)
			: this.getQueryForMacroContentFromBackend(props);
	};

	render() {
		const mode = window.location.pathname.startsWith('/wiki/pdf/') ? 'pdf' : this.props.mode;

		const { hasBodyBeenFetched } = this.state;
		const node = this.props.node;

		if (!node) {
			return null;
		}

		if (!hasBodyBeenFetched) {
			return <LegacyMacroRendererShadow node={node} />;
		}

		const adf = extensionToADF(node);

		return (
			<LegacyMacroRendererContext.Consumer>
				{({ shouldRefetchMacroQuery }) => (
					// In edit mode the editor renders this component outside of the main app, therefore
					// the Apollo client has to be provided here too

					// only use the multi macro query if this macro is not inside another, such as a page include
					// since that means it will not have been fetched by the multi macro query
					<ApolloProvider client={getApolloClient()}>
						<ExperienceTrackerContext.Provider value={getExperienceTracker()}>
							{this.determineQuery({ ...this.props, mode, adf, shouldRefetchMacroQuery })}
						</ExperienceTrackerContext.Provider>
					</ApolloProvider>
				)}
			</LegacyMacroRendererContext.Consumer>
		);
	}
}

export const doesIncludePageCircularRefExist = (
	listOfRenderedPageIncludeNodes: ExtensionWithParentContentId[],
) => {
	if (listOfRenderedPageIncludeNodes?.length) {
		// Keep reference so that we can render the previous page title in error message
		let prevRenderedNode;
		const pageIncludeNodeIds = new Set<string>();
		let pageIncludeNodeAlreadyRendered = false;
		for (const pageIncludeNode of listOfRenderedPageIncludeNodes) {
			if ((pageIncludeNode as ExtensionParams<any>)?.extensionKey !== 'include') {
				continue;
			}

			prevRenderedNode = pageIncludeNode;
			const id = pageIncludeNode?.parameters?.macroMetadata?.macroId?.value;
			if (id) {
				if (pageIncludeNodeIds.has(id)) {
					pageIncludeNodeAlreadyRendered = true;
					break;
				} else {
					pageIncludeNodeIds.add(id);
				}
			}
		}

		// indicate that this page has already been included, to avoid infinite loop
		if (pageIncludeNodeAlreadyRendered) {
			const prevReferencedNodePageTitle = prevRenderedNode?.parameters?.macroParams?.['']?.value;
			return { isCircularRef: true, prevPageTitle: prevReferencedNodePageTitle };
		}
	}

	return { isCircularRef: false, prevPageTitle: null };
};

type PageIncludeContextProviderProps = {
	contentId: any;
	node: any;
	listOfRenderedPageIncludeNodes: any;
	children: React.ReactNode;
};
const PageIncludeContextProvider = ({
	contentId,
	node,
	listOfRenderedPageIncludeNodes,
	children,
}: PageIncludeContextProviderProps) => {
	const contextValue = React.useMemo(() => {
		const renderedPageIncludeNodes = [
			...listOfRenderedPageIncludeNodes,
			{ ...node, parentContentId: contentId },
		];

		return { listOfRenderedPageIncludeNodes: renderedPageIncludeNodes };
	}, [listOfRenderedPageIncludeNodes, node, contentId]);

	return <PageIncludeContext.Provider value={contextValue}>{children}</PageIncludeContext.Provider>;
};

export const LegacyMacroRendererQuery = React.memo(
	(props: any) => {
		const { featureFlags } = useSessionData();

		return <LegacyMacroRendererQueryComponent featureFlags={featureFlags} {...props} />;
	},
	(prevProps, nextProps) => isEqual(prevProps, nextProps),
);

// This debounced component is used for bodied macros in live pages to help with multiplayer mode renders.
// If someone is editing the macro and another user is in view mode, the debounce helps prevent re-renders on every edit.
export const DebouncedLegacyMacroRendererQuery = debounceRender(LegacyMacroRendererQuery, 10000);
